import React from 'react'
import { Link } from 'react-router-dom'
import { DownloadIcon, LogOutIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { normalizeRole } from '../lib/permissions'
import {
  MobileListGroup,
  MobileGroupRow,
  MobilePage,
  MobilePageHeader,
  mobile,
} from '../components/mobile'
import { LanguageToggle } from '../components/LanguageToggle'
import { FieldTextSizeToggle } from '../components/FieldTextSizeToggle'

export function Profile() {
  const { t } = useTranslation(['settings', 'auth'])
  const { user, profile, signOut } = useAuth()
  const email = user?.email || profile?.email || ''
  const displayName =
    profile?.first_name || profile?.last_name
      ? `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
      : user?.user_metadata?.full_name || email.split('@')[0] || 'User'
  const roleLabel = normalizeRole(profile?.role).replace(/_/g, ' ')

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title={t('settings:profile.title')}
        subtitle={t('settings:profile.subtitle')}
      />

      <div className={`${mobile.surface} p-5 flex items-center gap-4`}>
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white text-lg font-bold">
          {displayName
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U'}
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{displayName}</p>
          <p className="text-sm text-gray-500 truncate">{email}</p>
          <p className="text-xs font-medium text-blue-700 mt-1 capitalize">{roleLabel}</p>
        </div>
      </div>

      <MobileListGroup label={t('settings:profile.account')}>
        <MobileGroupRow className="flex items-center gap-3">
          <UserIcon className="w-4 h-4 text-gray-400" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{t('settings:profile.signedInAs')}</p>
            <p className="text-xs text-gray-500 truncate">{email || '—'}</p>
          </div>
        </MobileGroupRow>
        <MobileGroupRow className="flex flex-col items-stretch gap-1 py-3">
          <LanguageToggle />
          <p className="text-xs text-gray-500 px-0.5">{t('settings:languageHint')}</p>
        </MobileGroupRow>
        <MobileGroupRow className="flex flex-col items-stretch gap-1 py-3">
          <FieldTextSizeToggle />
        </MobileGroupRow>
        <Link to="/install" className={`${mobile.row} flex items-center gap-3`}>
          <DownloadIcon className="w-4 h-4 text-gray-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{t('settings:profile.installApp')}</p>
            <p className="text-xs text-gray-500">{t('settings:profile.installHint')}</p>
          </div>
        </Link>
      </MobileListGroup>

      <button
        type="button"
        onClick={handleLogout}
        className={`${mobile.dangerBtn} w-full`}
      >
        <LogOutIcon className="w-4 h-4" />
        {t('settings:profile.signOut')}
      </button>
    </MobilePage>
  )
}

export default Profile
